import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { 
  Copy, 
  Eye, 
  EyeOff, 
  Power, 
  Server,
  Lock,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard as copyText } from '@/utils/clipboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MQTTCredentialsModal = ({ equipmentId, equipmentName, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [kicking, setKicking] = useState(false);
  const [kickDialogOpen, setKickDialogOpen] = useState(false);
  const [copied, setCopied] = useState({});

  useEffect(() => {
    fetchCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId]);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/equipments/${equipmentId}/mqtt-credentials`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching MQTT credentials:', error);
      if (error.response?.status === 404) {
        setCredentials(null);
      } else {
        toast.error(t('errorOccurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Estado "sem credenciais": gera via POST /mqtt-credentials/regenerate
  // (o endpoint cria quando não existem — _ensure_equipment_credentials).
  const handleRegenerate = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/equipments/${equipmentId}/mqtt-credentials/regenerate`);
      setCredentials(response.data);
      toast.success(t('saveSuccess'));
    } catch (error) {
      console.error('Error regenerating MQTT credentials:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleKick = async () => {
    try {
      setKicking(true);
      await axios.post(`${API}/equipments/${equipmentId}/mqtt/kick`);
      setKickDialogOpen(false);
      toast.success(t('mqttClientKicked'));
    } catch (error) {
      console.error('Error kicking client:', error);
      if (error.response?.status === 503) {
        toast.error(t('mqttServiceNotConnected'));
      } else {
        toast.error(t('errorOccurred'));
      }
    } finally {
      setKicking(false);
    }
  };

  const copyToClipboard = async (field, value) => {
    const success = await copyText(value);
    if (success) {
      setCopied({ ...copied, [field]: true });
      toast.success(`${field} ${t('copiedToClipboard')}`);
      setTimeout(() => {
        setCopied({ ...copied, [field]: false });
      }, 2000);
    } else {
      toast.info('Selecione o texto manualmente e use Ctrl+C');
    }
  };

  const generateConfigText = () => {
    if (!credentials) return '';
    
    return `# Configuração MQTT - ${equipmentName}
# =====================================

Broker: ${credentials.broker_host}
Porta: ${credentials.broker_port}
TLS: ${credentials.use_tls ? 'Sim' : 'Não'}

Usuário: ${credentials.mqtt_username}
Senha: ${credentials.mqtt_password || '[Senha oculta - regenere para ver]'}
Client ID: ${credentials.mqtt_client_id}

Tópico Base: ${credentials.mqtt_topic_base}

# Tópicos para publicação:
${credentials.mqtt_topic_base}/gps      - Dados GPS (lat, lon, speed)
${credentials.mqtt_topic_base}/engine   - Dados do motor (rpm, temp, fuel)
${credentials.mqtt_topic_base}/status   - Status do equipamento

# Exemplo de payload GPS (JSON):
{
  "lat": -23.5505,
  "lon": -46.6333,
  "speed": 12.5
}

# Exemplo Signal K (OpenPlotter):
{
  "updates": [{
    "values": [{
      "path": "navigation.position",
      "value": {"latitude": -23.5505, "longitude": -46.6333}
    }]
  }]
}
`;
  };

  const downloadConfig = () => {
    const config = generateConfigText();
    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mqtt-config-${credentials?.mqtt_client_id || 'equipment'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t('configDownloaded'));
  };

  const CredentialField = ({ label, value, field, isPassword = false }) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          type={isPassword && !showPassword ? 'password' : 'text'}
          value={value || (isPassword ? '••••••••••••••••' : '-')}
          className="font-mono text-sm bg-muted/50"
          data-testid={`mqtt-${field}`}
        />
        {isPassword && value && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowPassword(!showPassword)}
            data-testid="toggle-password"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => copyToClipboard(field, value)}
          disabled={!value}
          data-testid={`copy-${field}`}
        >
          {copied[field] ? (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-primary" />
              {t('mqttCredentials')} - {equipmentName}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !credentials ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
              <p className="text-muted-foreground">{t('noMqttCredentials')}</p>
              <Button onClick={handleRegenerate} className="mt-4">
                {t('generateCredentials')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Connection Info */}
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{t('brokerConnection')}</span>
                    <Badge variant={credentials.use_tls ? 'default' : 'secondary'}>
                      <Lock className="w-3 h-3 mr-1" />
                      {credentials.use_tls ? 'TLS' : 'No TLS'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Host</Label>
                      <p className="font-mono text-sm">{credentials.broker_host}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">{t('port')}</Label>
                      <p className="font-mono text-sm">{credentials.broker_port}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Credentials */}
              <div className="space-y-3">
                <CredentialField
                  label={t('mqttUsername')}
                  value={credentials.mqtt_username}
                  field="username"
                />
                <CredentialField
                  label={t('mqttPassword')}
                  value={credentials.mqtt_password}
                  field="password"
                  isPassword
                />
                <CredentialField
                  label={t('mqttClientId')}
                  value={credentials.mqtt_client_id}
                  field="client_id"
                />
                <CredentialField
                  label={t('mqttTopicBase')}
                  value={credentials.mqtt_topic_base}
                  field="topic"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadConfig}
                  className="gap-2"
                  data-testid="download-config"
                >
                  <FileText className="w-4 h-4" />
                  {t('downloadConfig')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setKickDialogOpen(true)}
                  className="gap-2"
                  data-testid="kick-client"
                >
                  <Power className="w-4 h-4" />
                  {t('killConnection')}
                </Button>
              </div>

              {/* Info about shared credentials */}
              <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
                <p className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {t('sharedCredentialsInfo') || 'Credenciais compartilhadas. A distinção dos equipamentos é feita pelo Client ID e Tópico.'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kick Confirmation */}
      <AlertDialog open={kickDialogOpen} onOpenChange={setKickDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('killConnection')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('killConnectionWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleKick} 
              disabled={kicking}
              className="bg-destructive hover:bg-destructive/90"
            >
              {kicking && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MQTTCredentialsModal;
